import { startTransition, useCallback, useEffect, useMemo, useState } from "react"

import { OwnerPortalLoadingSkeleton } from "@/components/app/public-loading-skeleton"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  buildPublicGrievanceHref,
  buildOwnerDetailRows,
  buildOwnerPortalGrievancePath,
  buildOwnerProfileUpdateRequest,
  createEmptyOwnerPortalFormState,
  createOwnerOtpChallenge,
  createOwnerPortalFormState,
  formatCountdown,
  formatDateTime,
  formatOwnerPortalStateLabel,
  formatPhone,
  formatTagType,
  getOwnerContactLabels,
  getOwnerIncidentLocation,
  getOwnerIncidentMediaSummary,
  getOwnerIncidentSubjectLabel,
  getOwnerIncidentSummary,
  getOwnerIncidentTitle,
  getPrimaryEmail,
  getPrimaryName,
  getPrimaryPhone,
  normalizePhoneInput,
  type OwnerOtpChallenge,
  type OwnerPortalFormState,
} from "@/features/public/my-device/owner-portal"
import {
  apiClient,
  getApiFailure,
  isApiError,
  type ComplianceSummary,
  type OwnerIncidentRecord,
  type OwnerPortalProfile,
  type PrivacyRequestRecord,
} from "@/lib/api/client"
import {
  clearOwnerPortalSession,
  persistOwnerPortalSession,
  restoreOwnerPortalSession,
  type OwnerPortalSession,
} from "@/lib/session/public-session"

interface PublicMyDevicePageProps {
  initialTag?: string | null
}

type OwnerPageView = "auth" | "denied" | "loading" | "ready"
type BannerTone = "error" | "info" | "success"

interface BannerState {
  message: string
  tone: BannerTone
}

const SECTION_CLASS = "shell-panel rounded-4xl border border-border/70 p-5 sm:p-6"
const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-28 resize-y`
const LABEL_CLASS = "text-sm font-medium text-foreground"
const HELP_CLASS = "mt-2 text-xs leading-5 text-muted-foreground"
const BANNER_BASE = "rounded-3xl border px-4 py-3 text-sm leading-6"

export function PublicMyDevicePage({
  initialTag = null,
}: PublicMyDevicePageProps) {
  const [view, setView] = useState<OwnerPageView>("auth")
  const [deviceTag, setDeviceTag] = useState(initialTag ?? "")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [authBanner, setAuthBanner] = useState<BannerState | null>(null)
  const [profileBanner, setProfileBanner] = useState<BannerState | null>(null)
  const [pendingChallenge, setPendingChallenge] =
    useState<OwnerOtpChallenge | null>(null)
  const [activeSession, setActiveSession] = useState<OwnerPortalSession | null>(
    null
  )
  const [profile, setProfile] = useState<OwnerPortalProfile | null>(null)
  const [history, setHistory] = useState<OwnerIncidentRecord[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyMessage, setHistoryMessage] = useState("")
  const [privacyRequests, setPrivacyRequests] = useState<PrivacyRequestRecord[]>(
    []
  )
  const [privacyMessage, setPrivacyMessage] = useState("")
  const [requestingOtp, setRequestingOtp] = useState(false)
  const [verifyingOtp, setVerifyingOtp] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [submittingPrivacyAction, setSubmittingPrivacyAction] = useState<
    "erasure" | "withdrawal" | null
  >(null)
  const [expandedPrivacyAction, setExpandedPrivacyAction] = useState<
    "erasure" | "withdrawal" | null
  >(null)
  const [privacyActionReason, setPrivacyActionReason] = useState({
    erasure: "",
    withdrawal: "",
  })
  const [form, setForm] = useState<OwnerPortalFormState>(() =>
    createEmptyOwnerPortalFormState()
  )
  const [tickNow, setTickNow] = useState(() => Date.now())
  const [deniedMessage, setDeniedMessage] = useState(
    "We could not load this SafetyQR profile."
  )

  useEffect(() => {
    if (!pendingChallenge) {
      return
    }

    const timerId = window.setInterval(() => {
      setTickNow(Date.now())
    }, 1000)

    return () => {
      window.clearInterval(timerId)
    }
  }, [pendingChallenge])

  const resendRemainingMs = useMemo(() => {
    if (!pendingChallenge) {
      return 0
    }

    return Math.max(0, pendingChallenge.resendAvailableAt - tickNow)
  }, [pendingChallenge, tickNow])

  const challengeExpiresAt = useMemo(() => {
    if (!pendingChallenge) {
      return 0
    }

    return Math.max(0, pendingChallenge.expiresAt - tickNow)
  }, [pendingChallenge, tickNow])

  const contactLabels = useMemo(
    () => getOwnerContactLabels(profile?.tagType ?? null),
    [profile?.tagType]
  )
  const detailRows = useMemo(() => buildOwnerDetailRows(profile), [profile])
  const historyFailed = historyMessage.length > 0
  const grievanceTag = profile?.tag || deviceTag.trim() || initialTag || null
  const ownerAuthManualReviewHref = buildOwnerPortalGrievancePath(grievanceTag)
  const privacyFailed = privacyMessage.length > 0
  const compliance = profile?.compliance ?? null
  const activeNoticeLabel = useMemo(() => {
    if (!compliance?.activeNoticeVersion) {
      return "No active notice recorded"
    }

    const version = compliance.activeNoticeVersion.version?.trim()
    const title = compliance.activeNoticeVersion.title?.trim()

    return [title, version].filter(Boolean).join(" • ") || "Recorded"
  }, [compliance])
  const hasGuardianContact = Boolean(
    compliance?.guardianName?.trim() || compliance?.guardianPhone?.trim()
  )
  const withdrawalLocked = Boolean(
    compliance?.consentStatus === "withdrawn"
      || compliance?.privacyStatus === "withdrawn"
      || compliance?.registrationStatus === "withdrawn"
      || compliance?.registrationStatus === "erasure_requested"
      || compliance?.registrationStatus === "erased"
  )
  const erasureLocked = Boolean(
    compliance?.registrationStatus === "erasure_requested"
      || compliance?.registrationStatus === "erased"
  )

  function updateForm<K extends keyof OwnerPortalFormState>(
    key: K,
    value: OwnerPortalFormState[K]
  ) {
    setForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }))
  }

  function updatePrivacyReason(
    action: "erasure" | "withdrawal",
    value: string
  ) {
    setPrivacyActionReason((currentReason) => ({
      ...currentReason,
      [action]: value,
    }))
  }

  async function refreshPrivacyRequests() {
    try {
      const response = await apiClient.listOwnerPrivacyRequests()
      setPrivacyRequests(response.data)
      setPrivacyMessage("")
    } catch (error) {
      if (isOwnerAuthError(error)) {
        throw error
      }

      setPrivacyMessage(
        error instanceof Error
          ? error.message
          : "Privacy request history could not be loaded right now."
      )
    }
  }

  function applyComplianceUpdate(nextCompliance: ComplianceSummary | null | undefined) {
    if (!nextCompliance) {
      return
    }

    setProfile((currentProfile) =>
      currentProfile
        ? {
            ...currentProfile,
            compliance: nextCompliance,
          }
        : currentProfile
    )
  }

  const resetPortal = useCallback(
    (options?: {
      keepBanner?: BannerState | null
      preservePhone?: string
      preserveTag?: string
    }) => {
    clearOwnerPortalSession()
    setActiveSession(null)
    setPendingChallenge(null)
    setOtpCode("")
    setProfile(null)
    setHistory([])
    setHistoryTotal(0)
    setHistoryMessage("")
    setPrivacyRequests([])
    setPrivacyMessage("")
    setExpandedPrivacyAction(null)
    setPrivacyActionReason({
      erasure: "",
      withdrawal: "",
    })
    setForm(createEmptyOwnerPortalFormState())
    setProfileBanner(null)
    setDeniedMessage("We could not load this SafetyQR profile.")
    setView("auth")
    setDeviceTag(options?.preserveTag ?? initialTag ?? "")
    setPhoneNumber(options?.preservePhone ?? "")
    setAuthBanner(options?.keepBanner ?? null)
    },
    [initialTag]
  )

  const loadOwnerPortal = useCallback(async (
    session: OwnerPortalSession,
    options?: { cancelledRef?: () => boolean }
  ) => {
    setView("loading")
    setAuthBanner(null)
    setProfileBanner(null)
    setDeniedMessage("We could not load this SafetyQR profile.")

    try {
      const profileEnvelope = await apiClient.getOwnerPortalProfile()

      if (options?.cancelledRef?.()) {
        return
      }

      let nextHistory: OwnerIncidentRecord[] = []
      let nextHistoryTotal = profileEnvelope.data.incidentCount ?? 0
      let nextHistoryMessage = ""
      let nextPrivacyRequests: PrivacyRequestRecord[] = []
      let nextPrivacyMessage = ""

      try {
        const historyEnvelope = await apiClient.getOwnerPortalIncidents()
        nextHistory = historyEnvelope.data
        nextHistoryTotal = historyEnvelope.total ?? historyEnvelope.data.length
      } catch (error) {
        if (isOwnerAuthError(error)) {
          throw error
        }

        nextHistoryMessage =
          error instanceof Error
            ? error.message
            : "Incident history could not be loaded right now."
      }

      try {
        const privacyEnvelope = await apiClient.listOwnerPrivacyRequests()
        nextPrivacyRequests = privacyEnvelope.data
      } catch (error) {
        if (isOwnerAuthError(error)) {
          throw error
        }

        nextPrivacyMessage =
          error instanceof Error
            ? error.message
            : "Privacy request history could not be loaded right now."
      }

      if (options?.cancelledRef?.()) {
        return
      }

      const persistedSession = persistOwnerPortalSession({
        ...session,
        kitId: profileEnvelope.data.kit?.id ?? session.kitId ?? null,
        phone: session.phone,
        stickerId: profileEnvelope.data.tag || session.stickerId,
        tagType: profileEnvelope.data.tagType ?? session.tagType,
      })

      startTransition(() => {
        setActiveSession(persistedSession)
        setProfile(profileEnvelope.data)
        setHistory(nextHistory)
        setHistoryTotal(nextHistoryTotal)
        setHistoryMessage(nextHistoryMessage)
        setPrivacyRequests(nextPrivacyRequests)
        setPrivacyMessage(nextPrivacyMessage)
        setForm(createOwnerPortalFormState(profileEnvelope.data))
        setView("ready")
      })
    } catch (error) {
      if (isOwnerAuthError(error)) {
        resetPortal({
          keepBanner: {
            message:
              error instanceof Error
                ? error.message
                : "Your owner verification expired. Start a new passcode flow to continue.",
            tone: "info",
          },
          preservePhone: session.phone ?? "",
          preserveTag: session.stickerId ?? initialTag ?? "",
        })
        return
      }

      clearOwnerPortalSession()
      setActiveSession(null)
      setDeniedMessage(
        error instanceof Error
          ? error.message
          : "We could not load this SafetyQR profile."
      )
      setView("denied")
    }
  }, [initialTag, resetPortal])

  useEffect(() => {
    let cancelled = false
    const restored = restoreOwnerPortalSession()

    if (restored.status === "restored") {
      setActiveSession(restored.session)
      setPhoneNumber(restored.session.phone ?? "")
      setDeviceTag(restored.session.stickerId ?? initialTag ?? "")

      void loadOwnerPortal(restored.session, {
        cancelledRef: () => cancelled,
      })

      return () => {
        cancelled = true
      }
    }

    if (restored.status === "expired") {
      setAuthBanner({
        message:
          "Your owner verification expired. Start a new passcode flow to continue.",
        tone: "info",
      })
    }

    if (!restored.session && restored.status !== "missing") {
      clearOwnerPortalSession()
    }

    setView("auth")

    return () => {
      cancelled = true
    }
  }, [initialTag, loadOwnerPortal])

  async function handleStartOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const trimmedTag = deviceTag.trim()
    const normalizedPhone = normalizePhoneInput(phoneNumber)

    if (!trimmedTag) {
      setAuthBanner({
        message: "Enter the SafetyQR tag before requesting a verification code.",
        tone: "error",
      })
      return
    }

    if (!normalizedPhone) {
      setAuthBanner({
        message: "Enter the phone number already associated with this SafetyQR profile.",
        tone: "error",
      })
      return
    }

    setRequestingOtp(true)
    setAuthBanner(null)

    try {
      const response = await apiClient.startOwnerPortalOtp({
        phoneNumber: normalizedPhone,
        tag: trimmedTag,
        tagType: profile?.tagType ?? activeSession?.tagType ?? null,
      })

      setPendingChallenge(
        createOwnerOtpChallenge({
          ...response.data,
          phoneNumber: normalizedPhone,
          tag: trimmedTag,
          tagType: profile?.tagType ?? activeSession?.tagType ?? null,
        })
      )
      setPhoneNumber(normalizedPhone)
      setOtpCode("")
      setAuthBanner({
        message: `A verification code was sent to ${formatPhone(normalizedPhone)}.`,
        tone: "info",
      })
    } catch (error) {
      const failure = getApiFailure(error)

      if (pendingChallenge && typeof failure?.resendAfterSec === "number") {
        setPendingChallenge((currentChallenge) =>
          currentChallenge
            ? {
                ...currentChallenge,
                resendAvailableAt:
                  Date.now() + Math.max(failure.resendAfterSec ?? 0, 0) * 1000,
              }
            : currentChallenge
        )
      }

      setAuthBanner({
        message:
          isApiError(error) && error.status === 403
            ? `${error.message} If the approved phone is no longer reachable, submit a tracked grievance instead of trying to bypass verification.`
            : error instanceof Error
              ? error.message
              : "Unable to start owner verification right now.",
        tone: "error",
      })
    } finally {
      setRequestingOtp(false)
    }
  }

  async function handleVerifyOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!pendingChallenge) {
      setAuthBanner({
        message: "Request a verification code before entering a passcode.",
        tone: "error",
      })
      return
    }

    if (!otpCode.trim()) {
      setAuthBanner({
        message: "Enter the verification code we sent to your phone.",
        tone: "error",
      })
      return
    }

    setVerifyingOtp(true)
    setAuthBanner(null)

    try {
      const response = await apiClient.verifyOwnerPortalOtp({
        otp: otpCode.trim(),
        phoneNumber: pendingChallenge.phoneNumber,
        verificationId: pendingChallenge.verificationId,
      })

      const ownerAuth = response.data.ownerAuth

      if (!response.data.approved || !ownerAuth?.token) {
        throw new Error("That passcode was not accepted. Check the code and try again.")
      }

      const session = persistOwnerPortalSession({
        ...ownerAuth,
        kitId: ownerAuth.kitId ?? null,
        phone: pendingChallenge.phoneNumber,
      })

      setActiveSession(session)
      setPendingChallenge(null)
      setOtpCode("")
      await loadOwnerPortal(session)
    } catch (error) {
      setAuthBanner({
        message:
          error instanceof Error
            ? error.message
            : "Unable to verify the passcode.",
        tone: "error",
      })
    } finally {
      setVerifyingOtp(false)
    }
  }

  async function handleResendOtp() {
    if (!pendingChallenge) {
      return
    }

    if (resendRemainingMs > 0) {
      return
    }

    setRequestingOtp(true)
    setAuthBanner(null)

    try {
      const response = await apiClient.startOwnerPortalOtp({
        phoneNumber: pendingChallenge.phoneNumber,
        tag: pendingChallenge.tag,
        tagType: pendingChallenge.tagType,
      })

      setPendingChallenge(
        createOwnerOtpChallenge({
          ...response.data,
          phoneNumber: pendingChallenge.phoneNumber,
          tag: pendingChallenge.tag,
          tagType: pendingChallenge.tagType,
        })
      )
      setOtpCode("")
      setAuthBanner({
        message: `A new code was sent to ${formatPhone(pendingChallenge.phoneNumber)}.`,
        tone: "info",
      })
    } catch (error) {
      setAuthBanner({
        message:
          error instanceof Error
            ? error.message
            : "Unable to resend the verification code.",
        tone: "error",
      })
    } finally {
      setRequestingOtp(false)
    }
  }

  async function handleSaveProfile(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!profile || !activeSession) {
      setProfileBanner({
        message: "Reload the owner portal before trying to save changes.",
        tone: "error",
      })
      return
    }

    setSavingProfile(true)
    setProfileBanner(null)

    try {
      const updatePayload = buildOwnerProfileUpdateRequest(profile, form)
      const updateResponse = await apiClient.updateOwnerPortalProfile(updatePayload)

      if (updateResponse.reauthRequired) {
        resetPortal({
          keepBanner: {
            message:
              "Your verification expired while saving. Start a new passcode flow to continue.",
            tone: "info",
          },
          preservePhone: activeSession.phone ?? "",
          preserveTag: activeSession.stickerId ?? profile.tag,
        })
        return
      }

      const refreshedProfile = await apiClient.getOwnerPortalProfile()
      const persistedSession = persistOwnerPortalSession({
        ...activeSession,
        kitId: refreshedProfile.data.kit?.id ?? activeSession.kitId ?? null,
        stickerId: refreshedProfile.data.tag || activeSession.stickerId,
        tagType: refreshedProfile.data.tagType ?? activeSession.tagType,
      })

      startTransition(() => {
        setActiveSession(persistedSession)
        setProfile(refreshedProfile.data)
        setForm(createOwnerPortalFormState(refreshedProfile.data))
        setProfileBanner({
          message: "Your SafetyQR registration details were updated successfully.",
          tone: "success",
        })
      })
    } catch (error) {
      if (isOwnerAuthError(error)) {
        resetPortal({
          keepBanner: {
            message:
              error instanceof Error
                ? error.message
                : "Your owner verification expired. Start a new passcode flow to continue.",
            tone: "info",
          },
          preservePhone: activeSession.phone ?? "",
          preserveTag: activeSession.stickerId ?? profile.tag,
        })
        return
      }

      setProfileBanner({
        message:
          error instanceof Error
            ? error.message
            : "Unable to save your SafetyQR updates.",
        tone: "error",
      })
    } finally {
      setSavingProfile(false)
    }
  }

  async function handlePrivacyAction(action: "erasure" | "withdrawal") {
    if (!profile || !activeSession) {
      setProfileBanner({
        message: "Reload the owner portal before submitting a privacy action.",
        tone: "error",
      })
      return
    }

    setSubmittingPrivacyAction(action)
    setProfileBanner(null)

    try {
      const response = (await apiClient.createOwnerPrivacyRequest({
        reason: privacyActionReason[action].trim() || null,
        requesterEmail: getPrimaryEmail(profile) || null,
        requesterName: getPrimaryName(profile) || null,
        requesterPhone: activeSession.phone ?? getPrimaryPhone(profile) ?? null,
        requestType: action,
      })) as {
        data: {
          compliance?: ComplianceSummary | null
          request: PrivacyRequestRecord
        }
      }

      applyComplianceUpdate(response.data.compliance)
      await refreshPrivacyRequests()
      setExpandedPrivacyAction(null)
      setPrivacyActionReason((currentReason) => ({
        ...currentReason,
        [action]: "",
      }))
      setProfileBanner({
        message:
          action === "withdrawal"
            ? "Consent was withdrawn for this SafetyQR profile. Public reporting is now suspended until a later re-consent flow exists."
            : "Your erasure request is now tracked. The profile has been suspended while deletion and redaction steps are processed.",
        tone: "success",
      })
    } catch (error) {
      if (isOwnerAuthError(error)) {
        resetPortal({
          keepBanner: {
            message:
              error instanceof Error
                ? error.message
                : "Your owner verification expired. Start a new passcode flow to continue.",
            tone: "info",
          },
          preservePhone: activeSession.phone ?? "",
          preserveTag: activeSession.stickerId ?? profile.tag,
        })
        return
      }

      setProfileBanner({
        message:
          error instanceof Error
            ? error.message
            : "Unable to submit the privacy action right now.",
        tone: "error",
      })
    } finally {
      setSubmittingPrivacyAction(null)
    }
  }

  return (
    <section className="grid gap-5">
      <Card className="shell-panel rounded-4xl border-border/70">
        <CardHeader className="gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary">Owner self-service</Badge>
            <Badge variant="outline">/my-device</Badge>
          </div>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
            <div className="flex flex-col gap-4">
              <CardTitle className="font-heading text-3xl leading-tight sm:text-4xl">
                <h2>
                  Verify your phone, review your device, and keep responder
                  details current.
                </h2>
              </CardTitle>
              <CardDescription className="max-w-3xl text-base leading-7 text-muted-foreground">
                Use this page to confirm your phone, review your device record,
                and keep important contact details up to date.
              </CardDescription>
            </div>
            <div className="grid gap-3 text-sm leading-6 text-muted-foreground">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3">
                We will send a verification code to confirm that you control this phone number.
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3">
                You can review your device details and update the information responders may need.
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3">
                Recent activity linked to this device stays visible in one place for quick follow-up.
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {view === "ready" && profile ? (
        <Card className="shell-panel rounded-4xl border-border/70">
          <CardContent className="flex flex-col gap-4 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2">
                <Badge variant="secondary">Verified session</Badge>
                <h2 className="font-heading text-2xl">Owner access is active</h2>
                <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                  Protected owner access is active for {profile.tag} using{" "}
                  {formatPhone(activeSession?.phone ?? getPrimaryPhone(profile))} in
                  this browser session.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetPortal({
                    preserveTag: initialTag ?? "",
                  })
                }}
              >
                Switch device
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {view === "auth" ? (
        <Card className={SECTION_CLASS}>
          <CardHeader className="px-0 pt-0">
            <CardTitle className="font-heading text-2xl">
              Start with your device tag and phone number
            </CardTitle>
            <CardDescription className="text-base leading-7 text-muted-foreground">
              We send a one-time passcode to the phone already associated with
              the SafetyQR profile for this device.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-5 px-0 pb-0">
            {authBanner ? <Banner banner={authBanner} /> : null}

            <form className="grid gap-4" onSubmit={handleStartOtp}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className={LABEL_CLASS}>SafetyQR tag</span>
                  <input
                    className={INPUT_CLASS}
                    aria-label="SafetyQR tag"
                    id="owner-device-tag"
                    name="deviceTag"
                    placeholder="Scan a tag or enter the printed code"
                    value={deviceTag}
                    onChange={(event) => {
                      setDeviceTag(event.target.value)
                      if (pendingChallenge) {
                        setPendingChallenge(null)
                      }
                    }}
                  />
                  <span className={HELP_CLASS}>
                    This should match the code printed on the SafetyQR sticker or
                    tag.
                  </span>
                </label>

                <label className="block">
                  <span className={LABEL_CLASS}>Phone number on file</span>
                  <input
                    className={INPUT_CLASS}
                    aria-label="Phone number on file"
                    id="owner-phone-number"
                    name="phoneNumber"
                    placeholder="+1 555 123 4567"
                    value={phoneNumber}
                    onChange={(event) => {
                      setPhoneNumber(event.target.value)
                      if (pendingChallenge) {
                        setPendingChallenge(null)
                      }
                    }}
                  />
                  <span className={HELP_CLASS}>
                    We use this to confirm you are the owner or primary contact
                    already tied to the device.
                  </span>
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={requestingOtp}>
                  {requestingOtp ? "Sending code..." : "Send verification code"}
                </Button>
              </div>
            </form>

            {pendingChallenge ? (
              <>
                <Separator />
                <form className="grid gap-4" onSubmit={handleVerifyOtp}>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-semibold">Enter the passcode</h3>
                    <p className="text-sm leading-6 text-muted-foreground">
                      Enter the code sent to{" "}
                      {formatPhone(pendingChallenge.phoneNumber)}.
                    </p>
                  </div>

                  {pendingChallenge.debugOtp ? (
                    <div className="rounded-3xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                      Development-only code: {pendingChallenge.debugOtp}
                    </div>
                  ) : null}

                  <label className="block">
                    <span className={LABEL_CLASS}>Verification code</span>
                    <input
                      aria-label="Verification code"
                      className={INPUT_CLASS}
                      id="owner-otp-code"
                      inputMode="numeric"
                      name="otpCode"
                      placeholder="123456"
                      value={otpCode}
                      onChange={(event) => setOtpCode(event.target.value)}
                    />
                  </label>

                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={verifyingOtp}>
                      {verifyingOtp ? "Verifying..." : "Verify and continue"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={requestingOtp || resendRemainingMs > 0}
                      onClick={() => void handleResendOtp()}
                    >
                      {requestingOtp ? "Resending..." : "Resend code"}
                    </Button>
                  </div>

                  <div className="grid gap-2 text-sm leading-6 text-muted-foreground">
                    <p>
                      {resendRemainingMs > 0
                        ? `You can request another code in ${formatCountdown(
                            Date.now() + resendRemainingMs
                          )}.`
                        : "You can request a new code now."}
                    </p>
                    {challengeExpiresAt > 0 ? (
                      <p>Code expires in {formatCountdown(Date.now() + challengeExpiresAt)}.</p>
                    ) : null}
                  </div>
                </form>
              </>
            ) : null}

            <div className="rounded-4xl border border-dashed border-border/70 bg-background/40 p-4">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <div className="grid gap-1">
                  <strong className="text-base">
                    Can&apos;t access the approved phone anymore?
                  </strong>
                  <p className="text-sm leading-6 text-muted-foreground">
                    Do not use an alternate or hidden verification path. Open a
                    tracked grievance so the case can be reviewed manually.
                  </p>
                </div>
                <Button asChild type="button" variant="outline">
                  <a href={ownerAuthManualReviewHref}>Open tracked manual review</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {view === "loading" ? (
        <OwnerPortalLoadingSkeleton testId="public-my-device-loading-skeleton" />
      ) : null}

      {view === "denied" ? (
        <Card className={SECTION_CLASS}>
          <CardHeader className="px-0 pt-0">
            <Badge variant="destructive">Owner access unavailable</Badge>
            <CardTitle className="font-heading text-2xl">
              We could not complete owner access
            </CardTitle>
            <CardDescription className="text-base leading-7 text-muted-foreground">
              {deniedMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3 px-0 pb-0">
            <Button asChild type="button">
              <a href={ownerAuthManualReviewHref}>Request tracked manual review</a>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {view === "ready" && profile ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="SafetyQR tag"
              note="Printed device identifier"
              value={profile.tag}
            />
            <SummaryCard
              label="Profile type"
              note="Registration model on file"
              value={formatTagType(profile.tagType)}
            />
            <SummaryCard
              label="Incidents"
              note="Recent history linked to this tag"
              value={String(historyTotal || profile.incidentCount || 0)}
            />
            <SummaryCard
              label="Open incidents"
              note="Pending items still linked to this tag"
              value={String(profile.openIncidentCount ?? 0)}
            />
          </section>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <Card className={SECTION_CLASS}>
              <CardHeader className="px-0 pt-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-2xl">
                      Consent and privacy state
                    </CardTitle>
                    <CardDescription className="text-base leading-7 text-muted-foreground">
                      Current rights state for this SafetyQR profile, including
                      the active notice reference and guardian authorization when
                      the profile belongs to a minor.
                    </CardDescription>
                  </div>
                  <Badge variant="outline">
                    {formatOwnerPortalStateLabel(
                      compliance?.registrationStatus ?? profile.device?.status
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 px-0 pb-0">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <ComplianceRow
                    label="Consent status"
                    value={formatOwnerPortalStateLabel(compliance?.consentStatus)}
                  />
                  <ComplianceRow
                    label="Privacy status"
                    value={formatOwnerPortalStateLabel(compliance?.privacyStatus)}
                  />
                  <ComplianceRow
                    label="Notice version"
                    value={activeNoticeLabel}
                  />
                  <ComplianceRow
                    label="Notice published"
                    value={formatDateTime(
                      compliance?.activeNoticeVersion?.publishedAt
                    )}
                  />
                  <ComplianceRow
                    label="Minor profile"
                    value={compliance?.isMinor ? "Yes" : "No"}
                  />
                  <ComplianceRow
                    label="Guardian approval"
                    value={formatOwnerPortalStateLabel(
                      compliance?.guardianApprovalStatus
                    )}
                  />
                </dl>

                {hasGuardianContact ? (
                  <div className="rounded-4xl border border-border/70 bg-background/65 p-4">
                    <div className="grid gap-2">
                      <span className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                        Guardian approved contact
                      </span>
                      <strong className="text-lg">
                        {compliance?.guardianName || "Guardian on file"}
                      </strong>
                      <div className="grid gap-1 text-sm text-muted-foreground">
                        <span>{formatPhone(compliance?.guardianPhone)}</span>
                        <span>
                          {compliance?.guardianRelationship || "Relationship not recorded"}
                        </span>
                        <span>{compliance?.guardianEmail || "No email on file"}</span>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-3 lg:grid-cols-2">
                  <PrivacyActionCard
                    action="withdrawal"
                    busy={submittingPrivacyAction === "withdrawal"}
                    description="Withdraw consent using the currently verified owner or guardian session. This suspends public reporting immediately."
                    disabled={withdrawalLocked}
                    expanded={expandedPrivacyAction === "withdrawal"}
                    lockedMessage="Consent is already withdrawn or this profile is already under erasure handling."
                    reason={privacyActionReason.withdrawal}
                    title="Withdraw consent"
                    onReasonChange={(value) =>
                      updatePrivacyReason("withdrawal", value)
                    }
                    onSubmit={() => void handlePrivacyAction("withdrawal")}
                    onToggle={() => {
                      setExpandedPrivacyAction((currentAction) =>
                        currentAction === "withdrawal" ? null : "withdrawal"
                      )
                    }}
                  />
                  <PrivacyActionCard
                    action="erasure"
                    busy={submittingPrivacyAction === "erasure"}
                    description="Create a tracked deletion or erasure request. This does not mean deletion is complete, but it suspends the profile immediately."
                    disabled={erasureLocked}
                    expanded={expandedPrivacyAction === "erasure"}
                    lockedMessage="An erasure request is already active for this profile."
                    reason={privacyActionReason.erasure}
                    title="Request deletion / erasure"
                    onReasonChange={(value) => updatePrivacyReason("erasure", value)}
                    onSubmit={() => void handlePrivacyAction("erasure")}
                    onToggle={() => {
                      setExpandedPrivacyAction((currentAction) =>
                        currentAction === "erasure" ? null : "erasure"
                      )
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className={SECTION_CLASS}>
              <CardHeader className="px-0 pt-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-2xl">
                      Privacy request timeline
                    </CardTitle>
                    <CardDescription className="text-base leading-7 text-muted-foreground">
                      Follow the recorded rights requests and their current
                      processing status from this owner portal session.
                    </CardDescription>
                  </div>
                  <Badge variant="outline">{privacyRequests.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 px-0 pb-0">
                {privacyFailed ? (
                  <Banner
                    banner={{
                      message:
                        privacyMessage
                        || "Privacy request history could not be loaded right now.",
                      tone: "info",
                    }}
                  />
                ) : null}

                {!privacyFailed && privacyRequests.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-border px-4 py-6 text-sm leading-6 text-muted-foreground">
                    No privacy requests are recorded for this SafetyQR profile yet.
                  </div>
                ) : null}

                {privacyRequests.map((request) => (
                  <article
                    key={request.id}
                    className="rounded-4xl border border-border/70 bg-background/65 p-4"
                  >
                    <div className="grid gap-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="grid gap-1">
                          <strong className="text-lg">
                            {formatOwnerPortalStateLabel(request.requestType)}
                          </strong>
                          <p className="text-sm leading-6 text-muted-foreground">
                            Created {formatDateTime(request.createdAt)}
                          </p>
                        </div>
                        <Badge variant="secondary">
                          {formatOwnerPortalStateLabel(request.status)}
                        </Badge>
                      </div>

                      {request.reason ? (
                        <p className="text-sm leading-6">{request.reason}</p>
                      ) : null}

                      <dl className="grid gap-3 sm:grid-cols-2">
                        <ComplianceRow
                          label="Acknowledged"
                          value={formatDateTime(request.acknowledgedAt)}
                        />
                        <ComplianceRow
                          label="Resolved"
                          value={formatDateTime(request.resolvedAt)}
                        />
                      </dl>

                      {request.resolutionSummary ? (
                        <div className="rounded-3xl border border-border/70 bg-background px-4 py-3 text-sm leading-6 text-muted-foreground">
                          {request.resolutionSummary}
                        </div>
                      ) : null}
                    </div>
                  </article>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)]">
            <Card className={SECTION_CLASS}>
              <CardHeader className="px-0 pt-0">
                <CardTitle className="font-heading text-2xl">
                  Device overview
                </CardTitle>
                <CardDescription className="text-base leading-7 text-muted-foreground">
                  Review the current responder-facing details on file for this
                  SafetyQR profile.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-5 px-0 pb-0">
                <div className="rounded-4xl border border-border/70 bg-background/65 p-4">
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                      Primary contact
                    </span>
                    <strong className="text-xl">{getPrimaryName(profile) || "Not available"}</strong>
                    <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                      <span>{formatPhone(getPrimaryPhone(profile))}</span>
                      <span>{getPrimaryEmail(profile) || "No email on file"}</span>
                    </div>
                  </div>
                </div>

                <dl className="grid gap-3 sm:grid-cols-2">
                  {detailRows.map((row) => (
                    <div
                      key={row.label}
                      className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3"
                    >
                      <dt className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                        {row.label}
                      </dt>
                      <dd className="mt-2 text-sm leading-6">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => {
                      window.location.assign(
                        `/report?tag=${encodeURIComponent(profile.tag)}`
                      )
                    }}
                  >
                    Report an incident
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      window.location.assign(
                        `/register?tag=${encodeURIComponent(profile.tag)}`
                      )
                    }}
                  >
                    Register another device
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className={SECTION_CLASS}>
              <CardHeader className="px-0 pt-0">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-2xl">
                      Recent linked reports
                    </CardTitle>
                    <CardDescription className="text-base leading-7 text-muted-foreground">
                      Review the recent incidents and updates connected to this device.
                    </CardDescription>
                  </div>
                  <Badge variant="outline">
                    {historyTotal || profile.incidentCount || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 px-0 pb-0">
                {historyFailed ? (
                  <Banner
                    banner={{
                      message:
                        historyMessage
                        || "Incident history could not be loaded right now.",
                      tone: "info",
                    }}
                  />
                ) : null}

                {!historyFailed && history.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-border px-4 py-6 text-sm leading-6 text-muted-foreground">
                    No incident history is currently linked to this SafetyQR tag.
                  </div>
                ) : null}

                {history.map((incident) => (
                  <article
                    key={incident.id}
                    className="rounded-4xl border border-border/70 bg-background/65 p-4"
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex flex-col gap-1">
                          <strong className="text-lg">
                            {getOwnerIncidentTitle(incident)}
                          </strong>
                          <p className="text-sm leading-6 text-muted-foreground">
                            Reported {formatDateTime(incident.createdAt)}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline">
                            {incident.reportKind ?? "incident"}
                          </Badge>
                          <Badge variant="outline">
                            {getOwnerIncidentSubjectLabel(incident)}
                          </Badge>
                          <Badge
                            variant={
                              incident.status === "resolved"
                                ? "secondary"
                                : incident.status === "pending"
                                  ? "outline"
                                  : "default"
                            }
                          >
                            {incident.status ?? "pending"}
                          </Badge>
                          <Badge variant="outline">
                            {incident.severity ?? "standard"}
                          </Badge>
                        </div>
                      </div>

                      <p className="text-sm leading-6">
                        {incident.description || "No description was included with this report."}
                      </p>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <FactCard
                          label="Location"
                          value={getOwnerIncidentLocation(incident)}
                        />
                        <FactCard
                          label="Subject context"
                          value={getOwnerIncidentSummary(incident)}
                        />
                        <FactCard
                          label="People involved"
                          value={
                            incident.peopleInvolved
                              ? `${incident.peopleInvolved} people involved`
                              : incident.injuryStatus
                                ? `Injury status: ${incident.injuryStatus}`
                                : "Reporter details limited"
                          }
                        />
                        <FactCard
                          label="Evidence"
                          value={
                            getOwnerIncidentMediaSummary(incident)
                            || "No media or updates linked"
                          }
                        />
                      </div>
                    </div>
                  </article>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card className={SECTION_CLASS}>
            <CardHeader className="px-0 pt-0">
              <CardTitle className="font-heading text-2xl">
                Update the fields responders or support staff may need
              </CardTitle>
              <CardDescription className="text-base leading-7 text-muted-foreground">
                Keep your registration details accurate so support teams can reach the right person quickly.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5 px-0 pb-0">
              {profileBanner ? <Banner banner={profileBanner} /> : null}

              <form className="grid gap-5" onSubmit={handleSaveProfile}>
                <section className="rounded-4xl border border-border/70 bg-background/65 p-4">
                  <div className="mb-4 grid gap-2">
                    <h3 className="text-lg font-semibold">Owner contact</h3>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {contactLabels.description}
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Field
                      label={contactLabels.name}
                      value={form.ownerName}
                      onChange={(value) => updateForm("ownerName", value)}
                    />
                    <Field
                      label={contactLabels.phone}
                      value={form.ownerPhone}
                      onChange={(value) => updateForm("ownerPhone", value)}
                    />
                    <Field
                      label={contactLabels.email}
                      type="email"
                      value={form.ownerEmail}
                      onChange={(value) => updateForm("ownerEmail", value)}
                    />
                  </div>
                </section>

                {profile.tagType === "fleet_tag" ? (
                  <section className="rounded-4xl border border-border/70 bg-background/65 p-4">
                    <div className="mb-4 grid gap-2">
                      <h3 className="text-lg font-semibold">Fleet assignment</h3>
                      <p className="text-sm leading-6 text-muted-foreground">
                        Keep fleet routing and assignee details aligned with the
                        existing device registration.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <Field
                        label="Fleet name"
                        value={form.fleetName}
                        onChange={(value) => updateForm("fleetName", value)}
                      />
                      <Field
                        label="Company name"
                        value={form.companyName}
                        onChange={(value) => updateForm("companyName", value)}
                      />
                      <Field
                        label="Assignee name"
                        value={form.assigneeName}
                        onChange={(value) => updateForm("assigneeName", value)}
                      />
                      <Field
                        label="Assignee email"
                        type="email"
                        value={form.assigneeEmail}
                        onChange={(value) => updateForm("assigneeEmail", value)}
                      />
                      <Field
                        label="Assignee phone"
                        value={form.assigneePhone}
                        onChange={(value) => updateForm("assigneePhone", value)}
                      />
                    </div>
                  </section>
                ) : null}

                {profile.tagType !== "personal_tag" ? (
                  <section className="rounded-4xl border border-border/70 bg-background/65 p-4">
                    <div className="mb-4 grid gap-2">
                      <h3 className="text-lg font-semibold">Vehicle summary</h3>
                      <p className="text-sm leading-6 text-muted-foreground">
                        Keep the identifying vehicle details aligned with the
                        SafetyQR profile.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <Field
                        label="Vehicle type"
                        value={form.vehicleType}
                        onChange={(value) => updateForm("vehicleType", value)}
                      />
                      <Field
                        label="Plate number"
                        value={form.plateNumber}
                        onChange={(value) => updateForm("plateNumber", value)}
                      />
                      <Field
                        label="Make"
                        value={form.make}
                        onChange={(value) => updateForm("make", value)}
                      />
                      <Field
                        label="Model"
                        value={form.model}
                        onChange={(value) => updateForm("model", value)}
                      />
                      <Field
                        label="Year"
                        value={form.year}
                        onChange={(value) => updateForm("year", value)}
                      />
                      <Field
                        label="Color"
                        value={form.color}
                        onChange={(value) => updateForm("color", value)}
                      />
                      {profile.tagType === "vehicle_sticker" ? (
                        <Field
                          label="VIN"
                          value={form.vin}
                          onChange={(value) => updateForm("vin", value)}
                        />
                      ) : null}
                    </div>
                  </section>
                ) : null}

                {profile.tagType !== "fleet_tag" ? (
                  <section className="rounded-4xl border border-border/70 bg-background/65 p-4">
                    <div className="mb-4 grid gap-2">
                      <h3 className="text-lg font-semibold">Emergency contact</h3>
                      <p className="text-sm leading-6 text-muted-foreground">
                        Keep a backup person reachable if responders or support
                        teams cannot reach you directly.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Emergency contact name"
                        value={form.emergencyContactName}
                        onChange={(value) =>
                          updateForm("emergencyContactName", value)
                        }
                      />
                      <Field
                        label="Emergency contact phone"
                        value={form.emergencyContactPhone}
                        onChange={(value) =>
                          updateForm("emergencyContactPhone", value)
                        }
                      />
                    </div>
                  </section>
                ) : null}

                {profile.tagType !== "fleet_tag" ? (
                  <section className="rounded-4xl border border-border/70 bg-background/65 p-4">
                    <div className="mb-4 grid gap-2">
                      <h3 className="text-lg font-semibold">Medical profile</h3>
                      <p className="text-sm leading-6 text-muted-foreground">
                        Update the medical details responders may need first.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <Field
                        label="Blood type"
                        value={form.bloodType}
                        onChange={(value) => updateForm("bloodType", value)}
                      />
                      <Field
                        label="Allergies"
                        value={form.allergies}
                        onChange={(value) => updateForm("allergies", value)}
                      />
                      <Field
                        label="Medical conditions"
                        value={form.medicalConditions}
                        onChange={(value) => updateForm("medicalConditions", value)}
                      />
                      <Field
                        label="Regular medications"
                        value={form.medications}
                        onChange={(value) => updateForm("medications", value)}
                      />
                    </div>

                    <label className="mt-4 block">
                      <span className={LABEL_CLASS}>Responder notes</span>
                      <textarea
                        className={TEXTAREA_CLASS}
                        value={form.responderNotes}
                        onChange={(event) =>
                          updateForm("responderNotes", event.target.value)
                        }
                      />
                    </label>
                  </section>
                ) : null}

                <div className="flex flex-wrap gap-3">
                  <Button type="submit" disabled={savingProfile}>
                    {savingProfile ? "Saving..." : "Save updates"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      ) : null}
      <OwnerPortalSupportSection
        ownerAuthManualReviewHref={ownerAuthManualReviewHref}
        tag={grievanceTag}
      />
    </section>
  )
}

function Field(props: {
  label: string
  onChange: (value: string) => void
  type?: string
  value: string
}) {
  const inputId = `owner-field-${props.label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`

  return (
    <label className="block" htmlFor={inputId}>
      <span className={LABEL_CLASS}>{props.label}</span>
      <input
        aria-label={props.label}
        className={INPUT_CLASS}
        id={inputId}
        type={props.type ?? "text"}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  )
}

function SummaryCard(props: { label: string; note: string; value: string }) {
  return (
    <Card className="shell-panel rounded-4xl border-border/70">
      <CardContent className="grid gap-2 px-5 py-5">
        <span className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {props.label}
        </span>
        <strong className="font-heading text-2xl">{props.value}</strong>
        <span className="text-sm leading-6 text-muted-foreground">
          {props.note}
        </span>
      </CardContent>
    </Card>
  )
}

function OwnerPortalSupportSection({
  ownerAuthManualReviewHref,
  tag,
}: {
  ownerAuthManualReviewHref: string
  tag: string | null
}) {
  return (
    <Card className={SECTION_CLASS}>
      <CardHeader className="px-0 pt-0">
        <CardTitle className="font-heading text-2xl">
          Need tracked help outside self-service?
        </CardTitle>
        <CardDescription className="text-base leading-7 text-muted-foreground">
          If you cannot access the approved OTP phone for this device, use the tracked
          manual-review path instead of looking for an alternate verification bypass.
          Operations will review the request and follow up through the grievance queue.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 px-0 pb-0 md:grid-cols-2 xl:grid-cols-5">
        <Button asChild key="owner-auth-manual-review" type="button">
          <a href={ownerAuthManualReviewHref}>Owner auth manual review</a>
        </Button>
        {[
          { caseType: "correction_help", label: "Request correction help" },
          { caseType: "withdrawal_help", label: "Request withdrawal help" },
          { caseType: "deletion_follow_up", label: "Follow up on deletion" },
          { caseType: "guardian_approval_help", label: "Get guardian approval help" },
        ].map((item) => (
          <Button asChild key={item.caseType} type="button" variant="outline">
            <a
              href={buildPublicGrievanceHref({
                caseType: item.caseType,
                source: "owner_portal",
                tag,
              })}
            >
              {item.label}
            </a>
          </Button>
        ))}
      </CardContent>
    </Card>
  )
}

function ComplianceRow(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3">
      <dt className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {props.label}
      </dt>
      <dd className="mt-2 text-sm leading-6">{props.value}</dd>
    </div>
  )
}

function FactCard(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/75 px-4 py-3">
      <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {props.label}
      </p>
      <p className="mt-2 text-sm leading-6">{props.value}</p>
    </div>
  )
}

function PrivacyActionCard(props: {
  action: "erasure" | "withdrawal"
  busy: boolean
  description: string
  disabled: boolean
  expanded: boolean
  lockedMessage: string
  reason: string
  title: string
  onReasonChange: (value: string) => void
  onSubmit: () => void
  onToggle: () => void
}) {
  return (
    <div className="rounded-4xl border border-border/70 bg-background/65 p-4">
      <div className="grid gap-3">
        <div className="grid gap-1">
          <strong className="text-lg">{props.title}</strong>
          <p className="text-sm leading-6 text-muted-foreground">
            {props.description}
          </p>
        </div>

        {props.disabled ? (
          <div className="rounded-3xl border border-border/70 bg-background px-4 py-3 text-sm leading-6 text-muted-foreground">
            {props.lockedMessage}
          </div>
        ) : null}

        {!props.disabled && props.expanded ? (
          <div className="grid gap-3">
            <label className="block">
              <span className={LABEL_CLASS}>
                Why are you submitting this {props.action} request?
              </span>
              <textarea
                aria-label={`${props.title} reason`}
                className={TEXTAREA_CLASS}
                value={props.reason}
                onChange={(event) => props.onReasonChange(event.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-3">
              <Button disabled={props.busy} type="button" onClick={props.onSubmit}>
                {props.busy ? "Submitting..." : props.title}
              </Button>
              <Button type="button" variant="outline" onClick={props.onToggle}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        {!props.disabled && !props.expanded ? (
          <Button type="button" variant="outline" onClick={props.onToggle}>
            {props.title}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function Banner(props: { banner: BannerState }) {
  const toneClass =
    props.banner.tone === "success"
      ? "border-emerald-300/80 bg-emerald-50 text-emerald-900"
      : props.banner.tone === "info"
        ? "border-sky-300/80 bg-sky-50 text-sky-900"
        : "border-rose-300/80 bg-rose-50 text-rose-900"

  return <div className={`${BANNER_BASE} ${toneClass}`}>{props.banner.message}</div>
}

function isOwnerAuthError(error: unknown) {
  return isApiError(error) && (error.status === 401 || error.status === 403)
}
